import { Box } from '@amzn/meridian';
import type { FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import { DestinationUrlInput } from 'features/campaign/campaign.types';
import { useEffect, useMemo, useRef } from 'react';
import { useRequiredFieldOnChange } from '../utils/useRequiredFieldOnChange';
import { LinkUrlSection } from './components/LinkUrlSection';
import { useUrlLogic } from './hooks/useUrlLogic';
import { generateUrlWithoutRefTag } from './utils';

/**
 * LinkUrlBuilder - Manages URL field only (no refTag logic)
 * Used in split-field schemas where URL and refTag are separate fields
 * Stores plain URL without refTag (e.g., '/kindle')
 * Note: Preview is handled by LinkRefTagBuilder when both widgets are present
 */
export const LinkUrlBuilder = <T = never, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = never>(
  props: WidgetProps<T, S, F>,
) => {
  const {
    onChange,
    value,
    registry: { formContext },
    required,
  } = props;

  const requiredFieldOnChange = useRequiredFieldOnChange(onChange, required);
  const lastSentUrlRef = useRef<string>(value || '');

  const { handleLinkTypeChange, handleLinkValueChange, handleShowVariationsChange, linkType, linkValue, showVariations, validationError } =
    useUrlLogic({
      platform: formContext?.platform,
      value,
    });

  const urlWithoutRefTag = useMemo(
    () => (linkType && linkValue ? generateUrlWithoutRefTag(linkType, linkValue, formContext?.platform, showVariations) : ''),
    [
      linkType,
      linkValue,
      formContext?.platform,
      showVariations,
    ],
  );

  useEffect(() => {
    if (urlWithoutRefTag !== lastSentUrlRef.current) {
      lastSentUrlRef.current = urlWithoutRefTag;
      requiredFieldOnChange(urlWithoutRefTag);
    }
  }, [
    requiredFieldOnChange,
    urlWithoutRefTag,
  ]);

  return (
    <Box>
      <LinkUrlSection
        linkType={linkType}
        linkValue={linkValue}
        onLinkTypeChange={handleLinkTypeChange}
        onLinkValueChange={handleLinkValueChange}
        onShowVariationsChange={linkType === DestinationUrlInput.DEALS ? handleShowVariationsChange : undefined}
        showVariations={linkType === DestinationUrlInput.DEALS ? showVariations : undefined}
        validationError={validationError}
      />
    </Box>
  );
};
